import {Overlay} from "@angular/cdk/overlay";
import {AbstractType, AfterViewInit, ChangeDetectorRef, Directive, EventEmitter, Injectable, InjectionToken, Injector, Input, OnChanges, OnDestroy, OnInit, SecurityContext, SimpleChanges, Type} from "@angular/core";
import {DomSanitizer, SafeHtml, SafeResourceUrl} from "@angular/platform-browser";
import {ActivatedRoute, NavigationEnd, NavigationError, NavigationExtras, NavigationStart, Router} from "@angular/router";

import {CacheService} from "framework/services/cache.service";
import {ErrorService} from "framework/services/error.service";
import {Lookup, UtilsService} from "framework/services/utils.service";
import {CommonLayout} from "framework/ui/layout";
import {AppNavigationService} from "framework/ui/navigation/app-navigation.service";
import {ViewState, ViewStateService} from "framework/ui/navigation/view-state.service";
import type {OverlayDialogRef} from "framework/ui/overlays/overlay-dialog";
import {PrintingService} from "framework/ui/reporting/printing.service";
import {PuppeteerService} from "framework/ui/reporting/puppeteer.service";
import {Future} from "framework/utils/concurrency";
import * as rx from "rxjs";

export interface ConfirmOperationHandler
{
    confirm(base: BaseComponent,
            text: string,
            title?: string): Promise<boolean>;
}

export const CONFIRM_OPERATION_HANDLER = new InjectionToken<ConfirmOperationHandler>("CONFIRM_OPERATION_HANDLER");

@Injectable()
export class ComponentCoreContext
{
    constructor(public injector: Injector)
    {}
}

@Injectable()
export class ComponentRoutingContext
{
    route: ActivatedRoute;

    router: Router;

    private m_isNavigating = false;
    private m_routerSubscription: rx.Subscription;

    constructor(public injector: Injector)
    {
        this.route  = injector.get(ActivatedRoute);
        this.router = injector.get(Router);

        this.m_routerSubscription = this.router.events.subscribe((event) =>
                                                                 {
                                                                     if (event instanceof NavigationStart)
                                                                     {
                                                                         this.m_isNavigating = true;
                                                                     }
                                                                     else if (event instanceof NavigationEnd || event instanceof NavigationError)
                                                                     {
                                                                         this.m_isNavigating = false;
                                                                     }
                                                                 });
    }

    navigate(commands: any[],
             extras?: NavigationExtras): Promise<boolean>
    {
        return this.router.navigate(commands, extras);
    }

    get isNavigating(): boolean
    {
        return this.m_isNavigating;
    }

    public onDestroy()
    {
        // Unsubscribe from navigation events once deleted
        this.m_routerSubscription.unsubscribe();
    }
}

@Injectable()
export class ComponentFrameworkContext
{
    constructor(public cache: CacheService,
                public errors: ErrorService,
                public utils: UtilsService)
    {}
}

@Injectable()
export class ComponentUIFrameworkContext
{
    constructor(public navigation: AppNavigationService,
                public viewstate: ViewStateService,
                public puppeteer: PuppeteerService,
                public printing: PrintingService,
                public overlay: Overlay)
    {}
}

@Injectable()
export class ComponentContext
{
    core: ComponentCoreContext;

    routing: ComponentRoutingContext;

    framework: ComponentFrameworkContext;

    ui: ComponentUIFrameworkContext;

    constructor(public injector: Injector)
    {
        this.core      = new ComponentCoreContext(injector);
        this.routing   = new ComponentRoutingContext(injector);
        this.framework = injector.get(ComponentFrameworkContext);
        this.ui        = injector.get(ComponentUIFrameworkContext);
    }

    inject<T>(token: Type<T>): T
    {
        return this.injector.get(token);
    }

    public showLoader(): void
    {
        document.getElementById("cs-loader").style.display = "block";
    }

    public hideLoader(): void
    {
        document.getElementById("cs-loader").style.display = "none";
    }

    public onDestroy(): void
    {
        // Clean up generated routing contexts
        this.routing.onDestroy();
    }
}

@Directive()
export abstract class BaseComponent implements OnInit,
                                               AfterViewInit,
                                               OnChanges,
                                               OnDestroy

{
    private static s_uniqueId            = 0;
    protected readonly uniqueComponentId = BaseComponent.s_uniqueId++;

    private m_cdr: ChangeDetectorRef;
    get cdr(): ChangeDetectorRef
    {
        return this.m_cdr;
    }

    get injector(): Injector
    {
        return this.inj;
    }

    private readonly m_componentContext: ComponentContext;
    get componentContext(): ComponentContext
    {
        return this.m_componentContext;
    }

    private readonly m_sanitizer: DomSanitizer;

    private m_vss: ViewStateService;

    private viewStateDefaultCache: Lookup<any> = {};
    private viewStateCacheReady                = false;

    @Input() viewStatePrefix: string;

    private eventSubscriptions: rx.Subscription[] = [];

    private readonly observableSubscriptions: Lookup<rx.Subscription> = {};

    private openOverlays: Lookup<OverlayDialogRef<any>> = {};

    private m_destroyed: boolean;

    private m_reportChanges: number;

    private m_layout: CommonLayout;

    protected readonly overridesAfterConfigurationChanges: boolean;

    constructor(private inj: Injector)
    {
        this.m_cdr              = inj.get(ChangeDetectorRef);
        this.m_vss              = inj.get(ViewStateService);
        this.m_sanitizer        = inj.get(DomSanitizer);
        this.m_layout           = inj.get(CommonLayout);
        this.m_componentContext = new ComponentContext(inj);

        this.overridesAfterConfigurationChanges = Object.getPrototypeOf(this).afterConfigurationChanges !==
                                                  BaseComponent.prototype.afterConfigurationChanges;

        this.subscribeToObservable(this.m_layout.contentSizeChanged, () => this.afterLayoutChange());
    }

    ngOnInit(): void
    {
    }

    ngAfterViewInit(): void
    {
    }

    ngOnDestroy(): void
    {
        this.cancelConfigurationChangesReport();

        this.m_destroyed = true;
        this.m_cdr       = null;

        // Clean up generated component contexts
        this.m_componentContext.onDestroy();

        for (let eventSubscription of this.eventSubscriptions)
        {
            eventSubscription.unsubscribe();
        }

        this.unsubscribeFromObservables();

        for (let overlayId in this.openOverlays)
        {
            this.openOverlays[overlayId]?.close(null);
        }
    }

    ngOnChanges(changes: SimpleChanges)
    {
        this.reportConfigurationChanges();
    }

    wasDestroyed(): boolean
    {
        return this.m_destroyed;
    }

    protected unsubscribeFromObservables()
    {
        for (let observableId in this.observableSubscriptions) this.unsubscribeFromObservable(observableId);
    }

    protected unsubscribeFromObservable(observableId: string)
    {
        const subscription = this.observableSubscriptions[observableId];
        if (subscription)
        {
            subscription.unsubscribe();
            delete this.observableSubscriptions[observableId];
        }
    }

    protected unsubscribeFromEvent(subscription: rx.Subscription)
    {
        const index = this.eventSubscriptions.indexOf(subscription);
        if (index >= 0)
        {
            this.eventSubscriptions.splice(index, 1)[0].unsubscribe();
        }
    }

    detectChanges()
    {
        this.m_cdr?.detectChanges();
    }

    markForCheck()
    {
        this.m_cdr?.markForCheck();
    }

    private cancelConfigurationChangesReport()
    {
        if (this.m_reportChanges)
        {
            this.m_componentContext.framework.utils.clearTimeoutOutsideAngular(this.m_reportChanges);
            this.m_reportChanges = undefined;
        }
    }

    protected reportConfigurationChanges()
    {
        if (this.overridesAfterConfigurationChanges)
        {
            this.cancelConfigurationChangesReport();

            this.m_reportChanges = this.m_componentContext.framework.utils.setTimeoutOutsideAngular(() =>
                                                                                                    {
                                                                                                        if (!this.m_destroyed) this.afterConfigurationChanges();
                                                                                                    }, 10);
        }
    }

    protected afterConfigurationChanges(): void
    {
        // Subclasses should override this method to get notified after configuration changes.
    }

    protected afterLayoutChange(): void
    {
        // Subclasses should override this method to get notified after layout changes.
    }

    protected reportLayoutChange()
    {
        this.m_layout.contentSizeChanged.next();
    }

    //--//

    protected inject<T>(token: Type<T> | InjectionToken<T> | AbstractType<T>): T
    {
        return this.inj.get(token);
    }

    //--//

    protected persistViewStateReady()
    {
        this.viewStateCacheReady = true;
    }

    public handleViewStateGetter(key: string)
    {
        if (!this.viewStateCacheReady)
        {
            throw `PersistViewState not properly initialized, did you forget to call "persistViewStateReady()?`;
        }

        return this.getViewStateValue(key, this.viewStateDefaultCache[key]);
    }

    public handleViewStateSetter(key: string,
                                 newVal: any,
                                 saveInBookmark: boolean)
    {
        // If we are still executing the constructor, we don't want to update the view state.
        if (!this.viewStateCacheReady)
        {
            this.viewStateDefaultCache[key] = newVal;
        }
        else
        {
            this.setViewStateValue(key, newVal, saveInBookmark);
        }
    }

    //--//

    public getViewState(): ViewState
    {
        let viewstate = this.m_vss.restore();
        if (this.viewStatePrefix)
        {
            return viewstate.getSubView(this.viewStatePrefix, true);
        }
        else
        {
            return viewstate;
        }
    }

    protected setViewStateValue(key: string,
                                value: any,
                                saveInBookmark: boolean = true)
    {
        let viewState = this.getViewState();
        viewState.set(key, value, saveInBookmark);
    }

    protected getViewStateValue<T>(key: string,
                                   defaultValue: T = null): T
    {
        let viewState = this.getViewState();
        return viewState.get(key, defaultValue);
    }

    protected removeViewStateKey(key: string)
    {
        let viewState = this.getViewState();
        viewState.remove(key);
    }

    protected removeViewStateKeys(prefix: string)
    {
        let viewState = this.getViewState();
        viewState.removeKeys(prefix);
    }

    protected async waitUntilTrue(retries: number,
                                  callback: () => boolean): Promise<boolean>
    {
        while (retries-- > 0)
        {
            if (this.wasDestroyed()) return false;

            if (callback()) return true;

            await Future.delayed(20);
        }

        return false;
    }

    //--//

    protected subscribeToEvent<T>(emitter: EventEmitter<T>,
                                  generatorOrNext?: (val: T) => void,
                                  error?: (err: any) => void,
                                  complete?: () => void): rx.Subscription
    {
        if (this.m_destroyed) return null;

        if (!emitter || !generatorOrNext) return null;

        let sub = emitter.subscribe(generatorOrNext, error, complete);
        this.eventSubscriptions.push(sub);
        return sub;
    }

    protected subscribeToObservable<T>(emitter: rx.Observable<T>,
                                       generatorOrNext: (val: T) => void,
                                       observableId?: string,
                                       error?: (err: any) => void,
                                       complete?: () => void): rx.Subscription
    {
        if (this.m_destroyed) return null;

        if (!emitter || !generatorOrNext) return null;

        const sub = emitter.subscribe(UtilsService.newObserver(generatorOrNext, error, complete));
        observableId ||= crypto.randomUUID();
        this.unsubscribeFromObservable(observableId);
        this.observableSubscriptions[observableId] = sub;

        return sub;
    }

    protected subscribeToObservableUntil<T>(emitter: rx.Observable<T>,
                                            generatorOrNext: (val: T) => void,
                                            until: () => boolean,
                                            error?: (err: any) => void,
                                            complete?: () => void): rx.Subscription
    {
        let sub: rx.Subscription;
        let compositeFn = (val: T) =>
        {
            if (until())
            {
                if (sub)
                {
                    sub.unsubscribe();
                    sub = null;
                }
            }
            else
            {
                generatorOrNext(val);
            }
        };

        return this.subscribeToObservable(emitter, compositeFn, null, error, complete);
    }

    registerOverlay(overlay: OverlayDialogRef<any>)
    {
        const id = overlay?.id;
        if (id) this.openOverlays[id] = overlay;
    }

    overlayClosed(overlay: OverlayDialogRef<any>)
    {
        const id = overlay?.id;
        if (id) delete this.openOverlays[id];
    }

    //--//

    protected subscribeToMouseDrag(target: HTMLElement,
                                   callback: (e: MouseEvent,
                                              mouseDown: boolean,
                                              mouseUp: boolean) => void)
    {
        const mousedown = fromEvent(target, "mousedown");
        const mousemove = fromEvent(document.documentElement, "mousemove");
        const mouseup   = fromEvent(document.documentElement, "mouseup");

        const mousedrag = mousedown.pipe(rx.mergeMap((e: MouseEvent) =>
                                                     {
                                                         callback(e, true, false);
                                                         return mousemove.pipe(rx.takeUntil(mouseup));
                                                     }));
        this.subscribeToObservable(mousedrag, (e: MouseEvent) => callback(e, false, false));

        this.subscribeToObservable(mouseup, (e: MouseEvent) => callback(e, false, true));
    }

    protected subscribeToTouchDrag(target: HTMLElement,
                                   callback: (e: TouchEvent,
                                              touchStart: boolean,
                                              touchEnd: boolean) => void)
    {
        const touchstart  = fromEvent(target, "touchstart");
        const touchmove   = fromEvent(document.documentElement, "touchmove");
        const touchend    = fromEvent(document.documentElement, "touchend");
        const touchcancel = fromEvent(document.documentElement, "touchcancel");
        const touchFinish = rx.merge(touchend, touchcancel);

        const touchdrag = touchstart.pipe(rx.mergeMap((e: TouchEvent) =>
                                                      {
                                                          callback(e, true, false);
                                                          return touchmove.pipe(rx.takeUntil(touchFinish));
                                                      }));
        this.subscribeToObservable(touchdrag, (e: TouchEvent) => callback(e, false, false));

        this.subscribeToObservable(touchFinish, (e: TouchEvent) => callback(e, false, true));
    }

    //--//

    protected sanitizeHtml(html: string): SafeHtml
    {
        return this.m_sanitizer.sanitize(SecurityContext.HTML, html);
    }

    protected bypassSecurityTrustHtml(html: string): SafeHtml
    {
        return this.m_sanitizer.bypassSecurityTrustHtml(html);
    }

    protected bypassSecurityTrustResourceUrl(html: string): SafeResourceUrl
    {
        return this.m_sanitizer.bypassSecurityTrustResourceUrl(html);
    }

    //--//

    protected matchFilter(target: string,
                          value: string): boolean
    {
        if (!target || !value) return false;

        value = value.toLocaleLowerCase();

        return value ? value.indexOf(target) >= 0 : false;
    }

    //--//

    public async confirmOperation(text: string,
                                  title?: string): Promise<boolean>
    {
        return this.inj.get(CONFIRM_OPERATION_HANDLER, null)?.confirm(this, text, title) ?? false;
    }
}

/**
 * This decorator persists and restores the property value from the component viewstate. Property must
 * be associated with a class deriving BaseComponent or exposing a "viewstate" property.
 */
export function PersistViewState(saveInBookmark: boolean = true,
                                 fixupFn?: (instance: BaseComponent,
                                            item: any) => void)
{
    return function (target: any,
                     key: string)
    {
        // Took inspiration from the implementation documented here
        // http://blog.wolksoftware.com/decorators-metadata-reflection-in-typescript-from-novice-to-expert-part-ii

        if (!(target instanceof BaseComponent))
        {
            throw `PersistViewState can only be applied to BaseComponent objects: found on property ${key} of ${target.constructor}`;
        }

        let prev: any;

        // property getter
        let getter = function (this: BaseComponent)
        {
            let value = this.handleViewStateGetter(key);

            if (fixupFn && prev !== value)
            {
                prev = value;
                fixupFn(this, value);
            }

            return value;
        };

        // property setter
        let setter = function (this: BaseComponent,
                               newVal: any)
        {
            return this.handleViewStateSetter(key, newVal, saveInBookmark);
        };

        // Delete property.
        if (delete (<any>target)[key])
        {
            // Create new property with getter and setter
            Object.defineProperty(target, key, {
                get         : getter,
                set         : setter,
                enumerable  : true,
                configurable: true
            });
        }
    };
}

export function fromEvent<K extends keyof WindowEventMap>(target: any,
                                                          type: K): rx.Observable<WindowEventMap[K]>
{
    return rx.fromEvent(target, type);
}
